import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { ActionTypeEnum, OptionsType, SELECT_ALL_ID } from '../../constants';
import StyledSelect from '../StyledSelect/index';

/**
 * Whether every option on offer is already chosen.
 *
 * Read by containment rather than by comparing two lengths. Two lists of the
 * same length are not the same list: a control may hold a value its options no
 * longer list — a record since closed, a name the server's cap cut — and a
 * caller may hand down a row that stands for others rather than being one of
 * them, which is what a group header is. Either way a count reaches the total
 * while something on screen is still unpicked, and the menu shuts on a person
 * who is mid-selection.
 *
 * `SelectAll`'s own synthetic row is the one exception the count already knew
 * about, and it stays excluded.
 */
const everythingChosen = (values, props) => {
  const valueOf = props.getOptionValue || (option => option.value);
  const selectable = props.options.filter(({ id }) => id !== SELECT_ALL_ID);

  if (!selectable.length) return false;

  const held = new Set((values || []).map(valueOf));

  return selectable.every(option => held.has(valueOf(option)));
};

/**
 * The changes that drop a value, after which the menu must stay shut.
 *
 * `clearValue` and `removeValue` both end in `focusInput()`, so a control that
 * opens its menu on focus would reopen it every time a chip is dropped from a
 * control that did not have focus — the opposite of what dropping one asks for.
 *
 * `pop-value` is deliberately absent. Backspace needs the input focused already
 * and `popValue` refocuses nothing, so there is no focus event for a flag to
 * suppress — arming one there only leaves it armed.
 */
const DISMISSING_ACTIONS = [ActionTypeEnum.CLEAR, ActionTypeEnum.REMOVE_VALUE];

/**
 * The multi variant of the design system's select: the menu survives a
 * selection, so several values can be picked in one visit.
 *
 * react-select closes its menu on every selection (`closeMenuOnSelect` defaults
 * to `true`, for multi as well as single), which is the one thing a multi-select
 * must not do. This holds `menuIsOpen` and drives it from react-select's own
 * `onMenuOpen` / `onMenuClose`, so the control's click-to-toggle, Escape and
 * outside-blur all still reach it — and a click on a chip's remove X does not,
 * because `MultiValueRemove` stops that mousedown before the control sees it.
 *
 * `blurInputOnSelect` goes with it. react-select defaults that one to
 * `isTouchCapable()`, which is true on every phone, on a touchscreen laptop and
 * in jsdom — so on those the control blurred itself after each pick, which the
 * blur handler below then reads as "the person left" and closes the menu. A
 * menu held open across a selection cannot also blur on one.
 *
 * Two departures from react-select's defaults, both pre-existing behaviour:
 *
 * - Focus opens the menu. react-select's `openMenuOnFocus` is off, so tabbing
 *   onto the control would otherwise land on a closed menu.
 * - Selecting the last unselected option closes the menu. Nothing is left to
 *   pick, and it is what makes `SelectAll`'s row read as a single action. See
 *   `everythingChosen` for what counts as the last one.
 *
 * Dropping a value is the exception to the first — see `DISMISSING_ACTIONS`.
 *
 * @internal
 */
const ControlledMultiSelect = ({ ...props }) => {
  // A ref rather than state, because the two ends of it can land in one React
  // batch: `removeValue` refocuses the input synchronously, so a state flag set
  // just above would still read `false` in the focus handler below and the menu
  // would open on the very change that asked it not to. `clearValue` refocuses
  // on a later tick and would have worked either way.
  const holdMenuClosed = useRef(false);
  const [isMenuOpen, setIsMenuOpen] = useState(props.menuIsOpen);

  const handleChange = (values, meta) => {
    if (
      (meta.action === ActionTypeEnum.SELECT_OPTION && meta.option.id === SELECT_ALL_ID) ||
      everythingChosen(values, props)
    ) {
      setIsMenuOpen(false);
    }

    if (DISMISSING_ACTIONS.includes(meta.action)) {
      holdMenuClosed.current = true;
    }

    props.onChange(values, meta);
  };

  const handleBlur = event => {
    setIsMenuOpen(false);
    // The suppression is spent here as well as on a focus, because the focus it
    // waits for does not always come. `focusInput()` on an already-focused input
    // fires no event, and `popValue` — Backspace on an empty input, which needs
    // focus by definition — never calls it at all. Left armed, the flag would sit
    // there and swallow the next real tab-in instead.
    holdMenuClosed.current = false;
    props.onBlur(event);
  };

  const handleFocus = event => {
    if (holdMenuClosed.current) {
      holdMenuClosed.current = false;
    } else {
      setIsMenuOpen(true);
    }

    props.onFocus(event);
  };

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
    props.onMenuOpen();
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    props.onMenuClose();
  };

  return (
    // A wrapper with no handler of its own, kept because consumers lay this
    // control out against the extra div.
    <Grid role="none">
      <StyledSelect
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        {...props}
        menuIsOpen={isMenuOpen}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onMenuClose={handleMenuClose}
        onMenuOpen={handleMenuOpen}
      />
    </Grid>
  );
};

ControlledMultiSelect.propTypes = {
  menuIsOpen: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  options: OptionsType,
};

ControlledMultiSelect.defaultProps = {
  menuIsOpen: false,
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  onMenuClose: () => {},
  onMenuOpen: () => {},
};

/**
 * @internal
 */
export default ControlledMultiSelect;
